import { ServiceResult } from "../types/api";
import { logger } from "../utils/logger";

/**
 * Turn a thrown error into the failure half of a ServiceResult.
 *
 * Replaces ~63 hand-written catch tails that had drifted into two
 * conventions ("An unexpected error occurred" vs "Failed to <verb> <thing>")
 * and three catch-binding spellings.
 *
 *   } catch (err) {
 *     return failure(err, "Failed to update user");
 *   }
 */
export function failure(err: unknown, fallback: string): ServiceResult<never> {
  // Logging lives here so it is consistent: previously roughly half the
  // catch tails logged and half swallowed silently (communityService's 16
  // failures produced no console output at all).
  logger.error(fallback, err);

  const message = err instanceof Error ? err.message : undefined;
  return { success: false, error: message || fallback };
}

/** The success half, so call sites read symmetrically with `failure`. */
export function success<T>(data?: T): ServiceResult<T> {
  return { success: true, data };
}

type QueryValue = string | number | boolean | null | undefined;

/**
 * Build a query string, skipping empty values.
 *
 * Booleans serialize as "1"/"0" - that is what the Laravel endpoints expect,
 * and hand-rolling it per service is where the `with_trashed` and
 * `is_challenge_participant` ladders came from.
 *
 * Returns "" (not "?") when nothing survives, so callers can concatenate
 * unconditionally.
 */
export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.append(
      key,
      typeof value === "boolean" ? (value ? "1" : "0") : String(value)
    );
  }

  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * The API marks deletion with `deleted_at` and the UI wants `is_active`.
 * This derivation was inlined at 12 call sites across three services.
 */
export function withIsActive<T extends { deleted_at?: string | null }>(
  record: T
): T & { is_active: boolean } {
  return { ...record, is_active: !record.deleted_at };
}
